import React, { useContext } from "react";
import { Tooltip } from "@cmsgov/ds-medicare-gov";
import { TooltipProps } from "@cmsgov/design-system/dist/types/Tooltip/Tooltip";
import classNames from "classnames";
import { IntlContext } from "../helpers/intlHooks";
import messages from "../translations/en-US.json";

interface TooltipWithCloseProps extends TooltipProps {
  /**
   * `headerText` is an optional heading for the tooltip's content. The required
   * `children` prop is used for the tooltip's trigger content (by default) and
   *  also the tooltip heading, unless `headerText` is also set.
   * If `headerText` is passed an empty string, no header will be displayed
   */
  headerText?: React.ReactNode;
  /**
   * `tagName` is an optional string representing an HTML element name. It
   * can be used to change the element that wraps the trigger of the
   * `Tooltip` component to create the custom `TooltipWithClose`.
   * The default is a `span`.
   */
  tagName?: keyof JSX.IntrinsicElements;
}

/**
 * Customized tooltip to match previous MPF implementation but leverage core DS
 * component functionality.
 *   existing implementation
 * - Uses as a prefix for the tooltip trigger `aria-label`, in this order:
 *     - the optional `ariaLabel` prop, which is a string
 *     - the optional `headerText` prop, if it's a string
 *     - `children`, if it's a string
 * - NB: The `title` prop is for dialog **content**
 *
 * For additional prop and usage documentation:
 * @see https://github.cms.gov/pages/MedicareGov/mgov-design-system/components/tooltip/
 * @see https://design.cms.gov/components/tooltip/
 */
export const TooltipWithClose = ({
  title,
  ariaLabel,
  headerText,
  children,
  className,
  activeClassName,
  maxWidth = "400px",
  tagName = "span",
  ...remainingProps
}: TooltipWithCloseProps) => {
  const WrappingComponent = tagName;
  const fm = useContext(IntlContext).formatMessage;

  //Get a string prefix to describe the tooltip trigger to screen readers
  let ariaLabelPrefix = "";
  if (ariaLabel) {
    // ariaLabel` is optional and a `string`
    ariaLabelPrefix = `${ariaLabel}: `;
  } else if (headerText && typeof headerText === "string") {
    // `headerText` is optional and a `ReactNode`
    ariaLabelPrefix = `${headerText}: `;
  } else if (typeof children === "string") {
    // `children` is requred and a `ReactNode`
    ariaLabelPrefix = `${children}: `;
  }

  const ariaLabelContent = fm
    ? `${ariaLabelPrefix}${fm({
        id: "tooltip_trigger_aria_label",
      })}`
    : `${ariaLabelPrefix}${messages.tooltip_trigger_aria_label}`;

  return (
    <WrappingComponent className="Tooltip Tooltip--with-close">
      <Tooltip
        title={
          <div className="Tooltip__body" data-testid="Tooltip__body">
            {title}
          </div>
        }
        contentHeading={
          headerText === "" ? (
            <span></span>
          ) : (
            <h2 className="ds-text-heading--lg ds-u-margin-bottom--0">
              {headerText || children}
            </h2>
          )
        }
        closeButtonLabel={fm ? fm({ id: "close" }) : "close"}
        showCloseButton={true}
        dialog={true}
        className={classNames(className, "Tooltip__trigger ds-c-button")}
        activeClassName={classNames(activeClassName, "Tooltip__trigger--open")}
        ariaLabel={ariaLabelContent}
        placement="top-start"
        maxWidth={maxWidth}
        {...remainingProps}
      >
        {children}
      </Tooltip>
    </WrappingComponent>
  );
};
